import { Avatar } from 'src/design/atoms/Avatar';
import { Badge } from 'src/design/atoms/Badge';
import { Center } from 'src/design/atoms/Center';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Tooltip } from 'src/design/atoms/Tooltip';
import {
  IconBarrierBlock,
  IconClockPlus,
  IconExclamationCircle,
} from '@tabler/icons-react';
import React from 'react';
import { formatDate } from 'src/utils';
import LinesEllipsis from 'react-lines-ellipsis';
import { useNavigate } from 'react-router-dom';
import { PostDto } from 'src/api/FiretrolApiClient';
import { LinkButton } from 'src/design/atoms/LinkButton';

interface ActivityFeedCardProps {
  title: string;
  items: PostDto[];
  openInNewTab?: boolean;
}

export const ActivityFeedCard: React.FC<ActivityFeedCardProps> = ({
  title,
  items,
  openInNewTab = false,
}) => {
  const navigate = useNavigate();
  return (
    <Paper>
      <Group justify="space-between" mb="md">
        <Title order={5}>{title}</Title>
        {items.length > 0 && (
          <Badge size="sm" variant="light" color="blue">
            {items.length} Open
          </Badge>
        )}
      </Group>
      <Stack gap="xs" style={{ height: 365, overflow: 'auto' }}>
        {items.length === 0 && (
          <Center style={{ marginTop: '50px' }}>
            <Text>No Open Items</Text>
          </Center>
        )}
        {items.map((item) => {
          return (
            <Group
              key={item.id}
              justify="space-between"
              p="xs"
              style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}
            >
              <Stack gap="xs" style={{ flex: 1 }}>
                <Text fw={500} size="sm">
                  <LinesEllipsis
                    text={`${
                      item.createdBy?.firstName
                    } ${item.createdBy?.lastName?.slice(0, 1)}. ${
                      item.plainTextContent
                    }`}
                    maxLine="2"
                    ellipsis="..."
                    trimRight
                    basedOn="letters"
                  />
                </Text>
                <Group gap="xs">
                  {item.constraint && (
                    <Tooltip label="Constraint">
                      <Avatar size="xs" color="blue">
                        <IconBarrierBlock size={16} />
                      </Avatar>
                    </Tooltip>
                  )}
                  {item.critical && (
                    <Tooltip label="Critical">
                      <Avatar size="xs" color="red">
                        <IconExclamationCircle size={16} />
                      </Avatar>
                    </Tooltip>
                  )}
                  {item.cost && (
                    <Tooltip label="Cost and Schedule">
                      <Avatar size="xs" color="blue">
                        <IconClockPlus size={16} />
                      </Avatar>
                    </Tooltip>
                  )}
                  {item.assignedTo && (
                    <Tooltip label="Assigned To">
                      <Badge size="xs" variant="light" color="blue">
                        {item.assignedTo.fullName}
                      </Badge>
                    </Tooltip>
                  )}
                  {item.date && (
                    <Tooltip label="Due Date">
                      <Badge size="xs" variant="light" color="blue">
                        {formatDate(item.date)}
                      </Badge>
                    </Tooltip>
                  )}
                  <LinkButton
                    size="compact-xs"
                    to={`/jobs/${item.jobId}/activity?postId=${item.id}`}
                    target={openInNewTab ? '_blank' : undefined}
                  >
                    Open
                  </LinkButton>
                </Group>
              </Stack>
            </Group>
          );
        })}
      </Stack>
    </Paper>
  );
};
